import React from "react";
import { LucideIcon } from "lucide-react";

interface ActivityCardInterface {
  name: string;
  img?: string; // Your PNG file name
  icon?: LucideIcon; // Optional Lucide Icon
  imgAsSrc?: boolean; // If true, it treats 'img' as raw content/src
  selected?: boolean;
  onClick?: () => void;
  color?: string; // Tailwind text color (e.g., 'text-blue-600')
  bgColor?: string; // Tailwind bg color (e.g., 'bg-blue-50')
}

export default function ActivityCard2({
  name,
  img,
  icon: Icon,
  imgAsSrc,
  selected,
  onClick,
  color = "text-slate-600",
  bgColor = "bg-slate-50",
}: ActivityCardInterface) {
  return (
    <div
      onClick={onClick}
      className={`group relative flex flex-col items-center justify-center p-4 sm:p-6 bg-white border transition-all duration-300 cursor-pointer overflow-hidden rounded-[2rem] shadow-sm hover:shadow-2xl hover:-translate-y-2
        ${selected ? "border-blue-500 ring-4 ring-blue-500/10 shadow-blue-900/5" : "border-slate-100 hover:border-slate-200"}
      `}
    >
      {/* Dynamic Background Glow */}
      <div
        className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${bgColor}`}
      />

      {/* Media Container (Icon or Image) */}
      <div
        className={`relative z-10 mb-3 flex items-center justify-center rounded-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-3
        ${!imgAsSrc ? `p-4 ${bgColor}` : "w-full h-24 sm:h-32"}
      `}
      >
        {/* Priority 1: Lucide Icon */}
        {Icon && (
          <Icon className={`size-8 sm:size-10 ${color}`} strokeWidth={1.5} />
        )}

        {/* Priority 2: Custom Image from /img folder */}
        {!Icon && img && !imgAsSrc && (
          <img
            src={`../../img/${img}.png`}
            alt={name}
            className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
          />
        )}

        {/* Priority 3: Raw Image/Source (if imgAsSrc is true) */}
        {!Icon && img && imgAsSrc && (
          <div className="w-full h-full overflow-hidden rounded-xl">
            <img src={img} alt={name} className="w-full h-full object-cover" />
          </div>
        )}
      </div>

      {/* Name / Label */}
      <div className="relative z-10">
        <h2
          className={`text-center font-bold tracking-tight transition-colors duration-300
          ${selected ? "text-blue-900" : "text-slate-700"}
          ${name === "Log Out" ? "text-red-600 group-hover:text-red-700" : ""}
          text-xs sm:text-sm md:text-base
        `}
        >
          {name}
        </h2>
      </div>

      {/* Selected Indicator Bar */}
      <div
        className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1.5 bg-blue-500 rounded-t-full transition-all duration-500
        ${selected ? "w-12 opacity-100" : "w-0 opacity-0 group-hover:w-8 group-hover:opacity-50"}
      `}
      />
    </div>
  );
}
